import React, { useState, useMemo } from 'react';
import { UserProfile, ClubEvent, Attendance, RankRequirement } from '../types';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { updateDoc, doc, setDoc } from 'firebase/firestore';
import { safeUserSearch } from '../lib/utils';
import { 
  Users, 
  UserPlus, 
  Search, 
  ShieldAlert, 
  Sparkles, 
  Ship, 
  Mail, 
  Phone, 
  Calendar, 
  CheckCircle2, 
  Award, 
  ExternalLink,
  Ban,
  UserCheck
} from 'lucide-react';
import { toast } from 'sonner';

interface GuestManagerProps {
  users: UserProfile[];
  events: ClubEvent[];
  attendance: Attendance[];
  ranks: RankRequirement[];
  onRefresh?: () => void;
  onSwitchToCrew?: () => void;
}

export default function GuestManager({ users, events, attendance, ranks, onRefresh, onSwitchToCrew }: GuestManagerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGuestForEnlist, setSelectedGuestForEnlist] = useState<UserProfile | null>(null);
  const [isAddGuestModalOpen, setIsAddGuestModalOpen] = useState(false);
  const [enlistDepartment, setEnlistDepartment] = useState('Operations');
  const [enlistRank, setEnlistRank] = useState('CRMN');

  // New Guest Form State
  const [newGuestName, setNewGuestName] = useState('');
  const [newGuestEmail, setNewGuestEmail] = useState('');
  const [newGuestPhone, setNewGuestPhone] = useState('');
  const [newGuestShip, setNewGuestShip] = useState('');
  const [newGuestNotes, setNewGuestNotes] = useState('');

  // Filter for guests and volunteers
  const guests = useMemo(() => {
    return users.filter(user => {
      const isExplicitGuest = user.roles?.includes('guest') || user.isGuest === true;
      const isVisitingShip = user.shipName && !user.shipName.toLowerCase().includes('zavala');
      return isExplicitGuest || isVisitingShip;
    });
  }, [users]);

  // Filtered by search query
  const filteredGuests = useMemo(() => {
    if (!searchQuery.trim()) return guests;
    return guests.filter(g => safeUserSearch(g, searchQuery));
  }, [guests, searchQuery]);

  // Handle Enlisting a Guest as Official Zavala Crew
  const handleConfirmEnlistment = async () => {
    if (!selectedGuestForEnlist) return;

    try {
      const today = new Date().toISOString().split('T')[0];
      const updatedRoles = (selectedGuestForEnlist.roles || [])
        .filter(r => r !== 'guest')
        .concat('member');

      await updateDoc(doc(db, 'users', selectedGuestForEnlist.uid), {
        roles: Array.from(new Set(updatedRoles)),
        isGuest: false,
        shipName: 'USS Zavala',
        currentRank: enlistRank,
        department: enlistDepartment,
        zavalaJoinDate: today,
        promotionRankDate: today,
        manualPoints: selectedGuestForEnlist.manualPoints || 0
      });

      toast.success(`${selectedGuestForEnlist.firstName} ${selectedGuestForEnlist.lastName} has been officially enlisted into the USS Zavala Crew!`);
      setSelectedGuestForEnlist(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'users');
    }
  };

  // Handle Adding a New Guest / Volunteer
  const handleCreateGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGuestName.trim() || !newGuestEmail.trim()) {
      toast.error('First & Last name and Email are required.');
      return;
    }

    const nameParts = newGuestName.trim().split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || 'Volunteer';
    const guestUid = `guest_${Date.now()}`;
    const today = new Date().toISOString().split('T')[0];

    const guestProfile: UserProfile = {
      uid: guestUid,
      firstName,
      lastName,
      email: newGuestEmail.trim().toLowerCase(),
      cell: newGuestPhone.trim(),
      shipName: newGuestShip.trim() || 'Visiting Volunteer / Non-Crew',
      roles: ['guest'],
      isGuest: true,
      currentRank: 'GUEST',
      zavalaJoinDate: today,
      promotionRankDate: today,
      manualPoints: 0,
      ots: false,
      occ: false,
      sdfpp: false,
      status: 'active'
    };

    try {
      await setDoc(doc(db, 'users', guestUid), guestProfile);
      toast.success(`Guest volunteer ${firstName} ${lastName} registered successfully.`);
      setIsAddGuestModalOpen(false);
      setNewGuestName('');
      setNewGuestEmail('');
      setNewGuestPhone('');
      setNewGuestShip('');
      setNewGuestNotes('');
      if (onRefresh) onRefresh();
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'users');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-[var(--background)] border-l-4 lg:border-l-8 border-lcars-gold pl-4 lg:pl-6 py-2">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-lcars-gold uppercase tracking-wider flex items-center gap-2">
              <Users className="w-6 h-6 text-lcars-gold" />
              Guests & Convention Volunteers Manifest
            </h2>
            <p className="text-xs text-[var(--foreground)]/50 uppercase tracking-widest font-mono mt-1">
              External Personnel • Convention Assistants • Visiting Starship Affiliates
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {onSwitchToCrew && (
              <button
                onClick={onSwitchToCrew}
                className="lcars-pill bg-lcars-blue text-black font-bold px-4 py-2 text-xs flex items-center gap-1.5 hover:bg-lcars-blue/80"
              >
                ← ZAVALA CREW MANIFEST
              </button>
            )}
            <button
              onClick={() => setIsAddGuestModalOpen(true)}
              className="lcars-pill bg-lcars-gold text-black font-bold px-5 py-2 text-xs flex items-center gap-2 hover:bg-lcars-gold/80"
            >
              <UserPlus className="w-4 h-4" />
              REGISTER NEW GUEST
            </button>
          </div>
        </div>
      </div>

      {/* Promotion Ineligibility Notice Alert */}
      <div className="bg-lcars-tan/10 border border-lcars-tan/40 rounded-xl p-4 flex items-start gap-3 text-xs">
        <Ban className="w-5 h-5 text-lcars-tan shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="font-bold text-lcars-tan uppercase tracking-wide">
            Promotion Eligibility Guard Active
          </p>
          <p className="text-[var(--foreground)]/80">
            Per USS Zavala chapter bylaws, guests and visiting convention volunteers are strictly in service support roles and are <strong>never eligible for Starfleet promotions</strong>. If a guest wishes to advance in rank and earn promotion points, use the <strong>Enlist to Zavala Crew</strong> action below to officially onboard them.
          </p>
        </div>
      </div>

      {/* Search and Stats Bar */}
      <div className="bg-black/40 border-2 border-white/10 rounded-xl p-4 flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-lcars-gold absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search guests by name, email, phone, or home starship..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full bg-black/60 border border-white/20 rounded-lg pl-9 pr-4 py-2 text-xs text-[var(--foreground)] focus:border-lcars-gold outline-none"
          />
        </div>

        <div className="flex items-center gap-4 text-xs font-mono text-[var(--foreground)]/60">
          <span>Total Guests: <strong className="text-lcars-gold font-bold">{guests.length}</strong></span>
          <span>Showing: <strong className="text-[var(--foreground)] font-bold">{filteredGuests.length}</strong></span>
        </div>
      </div>

      {/* Guests Table */}
      <div className="bg-black/40 border-2 border-lcars-gold rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="bg-white/5 border-b border-white/10 text-lcars-tan font-bold uppercase tracking-wider">
                <th className="p-4">Volunteer / Guest</th>
                <th className="p-4">Home Ship / Affiliation</th>
                <th className="p-4">Contact Info</th>
                <th className="p-4">Missions Assisted</th>
                <th className="p-4">Rank Status</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredGuests.length > 0 ? (
                filteredGuests.map(guest => {
                  const attendedCount = attendance.filter(a => a.userId === guest.uid && a.status === 'attending').length;
                  return (
                    <tr key={guest.uid} className="hover:bg-white/5 transition-colors">
                      <td className="p-4">
                        <div className="font-bold text-sm text-[var(--foreground)]">
                          {guest.firstName} {guest.lastName}
                        </div>
                        <span className="text-[0.65rem] bg-lcars-tan/20 text-lcars-tan px-2 py-0.5 rounded font-mono uppercase">
                          Guest Volunteer
                        </span>
                      </td>

                      <td className="p-4">
                        <div className="flex items-center gap-1.5 font-bold text-lcars-gold">
                          <Ship className="w-3.5 h-3.5 shrink-0" />
                          <span>{guest.shipName || 'No Affiliated Vessel'}</span>
                        </div>
                      </td>

                      <td className="p-4 space-y-1 font-mono text-[0.7rem]">
                        {guest.email && (
                          <div className="flex items-center gap-1.5 text-[var(--foreground)]/80">
                            <Mail className="w-3 h-3 text-lcars-blue shrink-0" />
                            <span>{guest.email}</span>
                          </div>
                        )}
                        {guest.cell && (
                          <div className="flex items-center gap-1.5 text-[var(--foreground)]/60">
                            <Phone className="w-3 h-3 text-lcars-tan shrink-0" />
                            <span>{guest.cell}</span>
                          </div>
                        )}
                      </td>

                      <td className="p-4">
                        <span className="bg-white/10 px-2.5 py-1 rounded-full font-bold font-mono text-xs">
                          {attendedCount} Events
                        </span>
                      </td>

                      <td className="p-4">
                        <span className="bg-lcars-red/20 text-lcars-red border border-lcars-red/30 px-2.5 py-1 rounded-full font-bold text-[0.65rem] uppercase">
                          PROMOTION INELIGIBLE
                        </span>
                      </td>

                      <td className="p-4 text-right">
                        <button
                          onClick={() => setSelectedGuestForEnlist(guest)}
                          className="lcars-pill bg-lcars-blue text-black font-bold px-3 py-1.5 text-xs hover:bg-lcars-blue/80 flex items-center gap-1.5 ml-auto"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          ENLIST TO CREW
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-[var(--foreground)]/40 font-mono uppercase tracking-widest">
                    No guests or volunteers found matching filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Enlistment Modal */}
      {selectedGuestForEnlist && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[var(--background)] border-4 border-lcars-blue rounded-2xl max-w-lg w-full p-6 space-y-5 shadow-2xl">
            <div className="border-b border-white/10 pb-3 flex justify-between items-center">
              <h3 className="text-xl font-bold text-lcars-blue uppercase tracking-wide flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-lcars-gold" />
                Enlist into USS Zavala Crew
              </h3>
              <button
                onClick={() => setSelectedGuestForEnlist(null)}
                className="text-[var(--foreground)]/40 hover:text-[var(--foreground)] font-bold"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-[var(--foreground)]/80">
              You are officially onboarding <strong>{selectedGuestForEnlist.firstName} {selectedGuestForEnlist.lastName}</strong> from Guest status into the USS Zavala Crew. This will grant them member access, enable point accumulation, and unlock the promotion ladder.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-[0.65rem] font-bold text-lcars-tan uppercase tracking-wider mb-1">
                  Assign Department
                </label>
                <select
                  value={enlistDepartment}
                  onChange={e => setEnlistDepartment(e.target.value)}
                  className="w-full bg-black/60 border border-white/20 rounded-lg p-2 text-xs text-[var(--foreground)] outline-none focus:border-lcars-blue"
                >
                  <option value="Command">Command</option>
                  <option value="Operations">Operations</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Communications">Communications</option>
                  <option value="Medical">Medical</option>
                  <option value="Science">Science</option>
                  <option value="Security">Security</option>
                </select>
              </div>

              <div>
                <label className="block text-[0.65rem] font-bold text-lcars-tan uppercase tracking-wider mb-1">
                  Initial Commission Rank
                </label>
                <select
                  value={enlistRank}
                  onChange={e => setEnlistRank(e.target.value)}
                  className="w-full bg-black/60 border border-white/20 rounded-lg p-2 text-xs text-[var(--foreground)] outline-none focus:border-lcars-blue"
                >
                  {ranks.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.name} ({r.id})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <button
                onClick={() => setSelectedGuestForEnlist(null)}
                className="lcars-pill bg-white/10 text-[var(--foreground)] font-bold px-4 py-2 text-xs hover:bg-white/20"
              >
                CANCEL
              </button>
              <button
                onClick={handleConfirmEnlistment}
                className="lcars-pill bg-lcars-blue text-black font-bold px-6 py-2 text-xs hover:bg-lcars-blue/80 flex items-center gap-1.5"
              >
                <UserCheck className="w-4 h-4" />
                CONFIRM ENLISTMENT
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Guest Modal */}
      {isAddGuestModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form onSubmit={handleCreateGuest} className="bg-[var(--background)] border-4 border-lcars-gold rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl">
            <div className="border-b border-white/10 pb-3 flex justify-between items-center">
              <h3 className="text-xl font-bold text-lcars-gold uppercase tracking-wide flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-lcars-gold" />
                Register Convention Guest / Volunteer
              </h3>
              <button
                type="button"
                onClick={() => setIsAddGuestModalOpen(false)}
                className="text-[var(--foreground)]/40 hover:text-[var(--foreground)] font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[0.65rem] font-bold text-lcars-tan uppercase tracking-wider mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={newGuestName}
                  onChange={e => setNewGuestName(e.target.value)}
                  placeholder="e.g. Jean-Luc Scott"
                  className="w-full bg-black/60 border border-white/20 rounded-lg p-2 text-xs text-[var(--foreground)] outline-none focus:border-lcars-gold"
                />
              </div>

              <div>
                <label className="block text-[0.65rem] font-bold text-lcars-tan uppercase tracking-wider mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={newGuestEmail}
                  onChange={e => setNewGuestEmail(e.target.value)}
                  placeholder="volunteer@example.com"
                  className="w-full bg-black/60 border border-white/20 rounded-lg p-2 text-xs text-[var(--foreground)] outline-none focus:border-lcars-gold"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[0.65rem] font-bold text-lcars-tan uppercase tracking-wider mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={newGuestPhone}
                    onChange={e => setNewGuestPhone(e.target.value)}
                    placeholder="555-0199"
                    className="w-full bg-black/60 border border-white/20 rounded-lg p-2 text-xs text-[var(--foreground)] outline-none focus:border-lcars-gold"
                  />
                </div>

                <div>
                  <label className="block text-[0.65rem] font-bold text-lcars-tan uppercase tracking-wider mb-1">
                    Home Ship / Chapter
                  </label>
                  <input
                    type="text"
                    value={newGuestShip}
                    onChange={e => setNewGuestShip(e.target.value)}
                    placeholder="e.g. USS Discovery, Fan Volunteer"
                    className="w-full bg-black/60 border border-white/20 rounded-lg p-2 text-xs text-[var(--foreground)] outline-none focus:border-lcars-gold"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => setIsAddGuestModalOpen(false)}
                className="lcars-pill bg-white/10 text-[var(--foreground)] font-bold px-4 py-2 text-xs hover:bg-white/20"
              >
                CANCEL
              </button>
              <button
                type="submit"
                className="lcars-pill bg-lcars-gold text-black font-bold px-6 py-2 text-xs hover:bg-lcars-gold/80"
              >
                REGISTER GUEST
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
